// Yamaha A3000 / A4000 / A5000 Native Format Floppy Disk Image Exporter
//
// Creates a standard 1.44 MB FAT12 floppy disk image (.img) containing
// Yamaha A-series native format sample files with the full directory
// structure (disk → volume → SBNK + SMPL).
//
// Directory structure on disk:
//   ROOT/
//     DISKHEX/           (disk folder, 8-char hex name)
//       0000             (disk info: volume name → Fxxx mapping + disk name ref)
//       F009             (disk name file: 16 bytes)
//       F001/            (volume folder)
//         SBNK/
//           0000          (sample name → Fxxx mapping)
//           F001          (sample 1 parameters — SBNK format)
//           F002          (sample 2 parameters)
//           ...
//         SMPL/
//           0000          (waveform name → Fxxx mapping)
//           F001          (sample 1 PCM data — SMPL format)
//           F002          (sample 2 PCM data)
//           ...
//
// SMPL file format (512-byte header + 16-bit signed BE PCM):
//   0x00  16  Magic "FSFSDEV3SPLXSMPL"
//   0x28  2   Sample rate (u16 big-endian)
//   0x54  16  Sample/waveform name (space-padded)
//   0x200 ... 16-bit signed big-endian PCM data
//
// SBNK file format (356 bytes):
//   0x00  16  Magic "FSFSDEV3SPLXSBNK"
//   0x12  1   Original key (MIDI note, default 60)
//   0x13  1   Fine tune (signed, default 0)
//   0x14  1   Level (0-127, default 100)
//   0x32  16  Sample name (space-padded)
//   0x78  16  Left waveform name (space-padded)
//   0x88  16  Right waveform name (zeros if mono)
//
// 0000 file format (32 bytes per entry):
//   0x00  1   Flag byte (0)
//   0x01  16  Name (space-padded)
//   0x11  1   Flag byte (0)
//   0x12  14  Fxxx path (null-padded)
//
// Works with Gotek floppy emulators and real Yamaha A3000/A4000/A5000.
//
// Format reference: mybunnyhug.org/fileformats/yamahaaseries/,
// pyakdisk by Stephen Norum.

// ---- FAT12 1.44MB floppy geometry (same as MPC) ---------------------------
const DISK_SIZE    = 1474560;   // 1.44 MB (2880 × 512)
const SECTOR_SIZE  = 512;
const TOTAL_SECTORS = 2880;
const SECTORS_PER_CLUSTER = 1;
const RESERVED_SECTORS = 1;
const NUM_FATS = 2;
const SECTORS_PER_FAT = 9;
const ROOT_ENTRIES = 224;
const ROOT_DIR_SECTORS = 14;    // 224 × 32 / 512
const MEDIA_DESCRIPTOR = 0xF0;  // 3.5" HD floppy

const DATA_START_SECTOR = RESERVED_SECTORS + NUM_FATS * SECTORS_PER_FAT + ROOT_DIR_SECTORS;
const DATA_START_BYTE = DATA_START_SECTOR * SECTOR_SIZE;
const DATA_BYTES = DISK_SIZE - DATA_START_BYTE;
const MAX_CLUSTERS = Math.floor(DATA_BYTES / SECTOR_SIZE);

const ROOT_DIR_OFFSET = (RESERVED_SECTORS + NUM_FATS * SECTORS_PER_FAT) * SECTOR_SIZE;
const ROOT_DIR_BYTES = ROOT_ENTRIES * 32;

const FAT1_OFFSET = RESERVED_SECTORS * SECTOR_SIZE;
const FAT2_OFFSET = (RESERVED_SECTORS + SECTORS_PER_FAT) * SECTOR_SIZE;
const FAT_BYTES = SECTORS_PER_FAT * SECTOR_SIZE;

// ---- Yamaha format constants -----------------------------------------------
const YAMAHA_HEADER_SIZE = 0x200; // 512 bytes
const YAMAHA_MAGIC_SMPL = 'FSFSDEV3SPLXSMPL';
const YAMAHA_MAGIC_SBNK = 'FSFSDEV3SPLXSBNK';
const YAMAHA_SBNK_FILE_SIZE = 356;
const YAMAHA_SAMPLE_RATE = 44100;
const YAMAHA_NAME_LENGTH = 16;

// ---- FAT12 helpers ---------------------------------------------------------

function setFat12Entry(fat, cluster, value) {
   const offset = cluster + (cluster >> 1);
   if (cluster & 1) {
      fat[offset] = (fat[offset] & 0x0F) | ((value & 0x0F) << 4);
      fat[offset + 1] = (value >> 4) & 0xFF;
   } else {
      fat[offset] = value & 0xFF;
      fat[offset + 1] = (fat[offset + 1] & 0xF0) | ((value >> 8) & 0x0F);
   }
}

function writeAscii(buf, off, len, str) {
   const s = (str || '').substring(0, len).padEnd(len, ' ');
   for (let i = 0; i < len; i++) {
      buf[off + i] = s.charCodeAt(i) & 0xFF;
   }
}

function toFatName(name, ext) {
   const clean = (name || 'SAMPLE').toUpperCase().replace(/[^A-Z0-9_]/g, '').substring(0, 8);
   const cleanExt = (ext || '').toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 3);
   return { name: clean.padEnd(8, ' '), ext: cleanExt.padEnd(3, ' ') };
}

function buildDirEntry(name, ext, startCluster, fileSize, isDir) {
   const entry = new Uint8Array(32);
   const dv = new DataView(entry.buffer);
   writeAscii(entry, 0, 8, name);
   writeAscii(entry, 8, 3, ext);
   entry[11] = isDir ? 0x10 : 0x20;
   dv.setUint16(16, 0x0000 | (12 << 11) | (0 << 5) | 0, true);
   dv.setUint16(18, ((2024 - 1980) << 9) | (1 << 5) | 1, true);
   dv.setUint16(20, startCluster & 0xFFFF, true);
   dv.setUint32(22, fileSize, true);
   return entry;
}

function buildBootSector(volumeLabel) {
   const boot = new Uint8Array(SECTOR_SIZE);
   const dv = new DataView(boot.buffer);
   boot[0] = 0xEB; boot[1] = 0x3C; boot[2] = 0x90;
   writeAscii(boot, 3, 8, 'MSDOS5.0');
   dv.setUint16(11, SECTOR_SIZE, true);
   boot[13] = SECTORS_PER_CLUSTER;
   dv.setUint16(14, RESERVED_SECTORS, true);
   boot[16] = NUM_FATS;
   dv.setUint16(17, ROOT_ENTRIES, true);
   dv.setUint16(19, TOTAL_SECTORS, true);
   boot[21] = MEDIA_DESCRIPTOR;
   dv.setUint16(22, SECTORS_PER_FAT, true);
   dv.setUint16(24, 18, true);
   dv.setUint16(26, 2, true);
   dv.setUint32(28, 0, true);
   dv.setUint32(32, 0, true);
   boot[36] = 0x00;
   boot[37] = 0x00;
   boot[38] = 0x29;
   dv.setUint32(39, 0x12345678, true);
   writeAscii(boot, 43, 11, (volumeLabel || 'YAMAHA     ').padEnd(11, ' ').substring(0, 11));
   writeAscii(boot, 54, 8, 'FAT12   ');
   boot[510] = 0x55;
   boot[511] = 0xAA;
   return boot;
}

// ---- Yamaha file builders --------------------------------------------------

/**
 * Build a Yamaha SMPL file (512-byte header + 16-bit BE PCM).
 */
export function buildSmplFile(name, pcm, sampleRate = YAMAHA_SAMPLE_RATE) {
   const pcmBytes = pcm.length * 2;
   const fileSize = YAMAHA_HEADER_SIZE + pcmBytes;
   const buf = new Uint8Array(fileSize);
   const dv = new DataView(buf.buffer);

   // Magic
   writeAscii(buf, 0, 16, YAMAHA_MAGIC_SMPL);

   // Sample rate (u16 big-endian)
   dv.setUint16(0x28, sampleRate, false);

   // Sample/waveform name (16 bytes, space-padded)
   const smplName = (name || 'SAMPLE').substring(0, YAMAHA_NAME_LENGTH);
   writeAscii(buf, 0x54, YAMAHA_NAME_LENGTH, smplName.padEnd(YAMAHA_NAME_LENGTH, ' '));

   // PCM data: 16-bit signed big-endian
   for (let i = 0; i < pcm.length; i++) {
      dv.setInt16(YAMAHA_HEADER_SIZE + i * 2, pcm[i], false);
   }

   return buf;
}

/**
 * Build a Yamaha SBNK parameter file (356 bytes).
 */
export function buildSbnkFile(sampleName, waveName, opts = {}) {
   const buf = new Uint8Array(YAMAHA_SBNK_FILE_SIZE);
   const dv = new DataView(buf.buffer);

   // Magic
   writeAscii(buf, 0, 16, YAMAHA_MAGIC_SBNK);

   // Original key (MIDI note, default 60 = C3)
   buf[0x12] = opts.originalKey || 60;

   // Fine tune (signed, default 0)
   buf[0x13] = opts.fineTune || 0;

   // Level (0-127, default 100)
   buf[0x14] = opts.level || 100;

   // Sample name at 0x32 (16 bytes)
   const sName = (sampleName || 'SAMPLE').substring(0, YAMAHA_NAME_LENGTH);
   writeAscii(buf, 0x32, YAMAHA_NAME_LENGTH, sName.padEnd(YAMAHA_NAME_LENGTH, ' '));

   // Left waveform name at 0x78 (16 bytes)
   const wName = (waveName || sampleName || 'SAMPLE').substring(0, YAMAHA_NAME_LENGTH);
   writeAscii(buf, 0x78, YAMAHA_NAME_LENGTH, wName.padEnd(YAMAHA_NAME_LENGTH, ' '));

   // Right waveform name at 0x88 (16 bytes) — zeros for mono
   // Already zero-initialized

   return buf;
}

/**
 * Build a 0000 information file.
 * Each entry is 32 bytes: flag(1) + name(16) + flag(1) + path(14)
 */
export function build0000File(entries) {
   const buf = new Uint8Array(entries.length * 32);
   for (let i = 0; i < entries.length; i++) {
      const off = i * 32;
      // Flag byte 0 (ignored by parser)
      buf[off] = 0;
      // Name (16 bytes, space-padded)
      const name = (entries[i].name || '').substring(0, 16);
      writeAscii(buf, off + 1, 16, name.padEnd(16, ' '));
      // Flag byte 1 (ignored by parser)
      buf[off + 17] = 0;
      // Fxxx path (14 bytes, null-padded)
      const path = (entries[i].path || '').substring(0, 14);
      writeAscii(buf, off + 18, 14, path);
   }
   return buf;
}

/**
 * Build a disk name file (Fyyy) — just 16 bytes of the disk name.
 */
export function buildDiskNameFile(diskName) {
   const buf = new Uint8Array(16);
   const name = (diskName || 'YAMAHA DISK').substring(0, 16);
   writeAscii(buf, 0, 16, name.padEnd(16, ' '));
   return buf;
}

// ---- Capacity calculation --------------------------------------------------

export function calcYamahaCapacity(samples) {
   let total = 0;
   for (const smp of samples) {
      if (!smp.pcm || smp.pcm.length === 0) continue;
      // SMPL file: 512-byte header + 2 bytes per sample
      total += YAMAHA_HEADER_SIZE + smp.pcm.length * 2;
      // SBNK file: 356 bytes
      total += YAMAHA_SBNK_FILE_SIZE;
   }
   // Add directory overhead: disk dir (1 cluster) + 0000 file (1 cluster) +
   // F009 file (1 cluster) + volume dir (1 cluster) + SBNK dir (1+ clusters) +
   // SMPL dir (1+ clusters) + SBNK 0000 (1 cluster) + SMPL 0000 (1 cluster)
   const dirClusters = 8 + Math.ceil(samples.length / 16) * 2; // rough estimate
   total += dirClusters * SECTOR_SIZE;
   return total;
}

export function calcYamahaLayout(samples) {
   const totalBytes = calcYamahaCapacity(samples);
   const maxBytes = DATA_BYTES;
   return { totalBytes, fits: totalBytes <= maxBytes, maxBytes };
}

// ---- FAT12 filesystem builder with directory support -----------------------

/**
 * Build a complete Yamaha A-series FAT12 floppy disk image.
 *
 * @param {Array} samples - Array of { name, pcm: Int16Array, sampleRate }
 * @param {object} opts - { diskName: string, volumeName: string }
 * @returns {Uint8Array} 1,474,560-byte disk image
 */
export function buildYamahaDisk(samples, opts = {}) {
   const diskName = (opts.diskName || 'YAMAHA DISK').substring(0, 16);
   const volumeName = (opts.volumeName || 'VOLUME 1').substring(0, 16);
   const validSamples = samples.filter(s => s.pcm && s.pcm.length > 0);

   if (validSamples.length === 0) {
      throw new Error('No valid samples to export');
   }

   const disk = new Uint8Array(DISK_SIZE);

   // ---- Boot sector --------------------------------------------------------
   const boot = buildBootSector(diskName);
   disk.set(boot, 0);

   // ---- FAT initialization -------------------------------------------------
   const fat1 = disk.subarray(FAT1_OFFSET, FAT1_OFFSET + FAT_BYTES);
   const fat2 = disk.subarray(FAT2_OFFSET, FAT2_OFFSET + FAT_BYTES);
   // Entry 0: media descriptor
   fat1[0] = MEDIA_DESCRIPTOR; fat1[1] = 0xFF; fat1[2] = 0xFF;
   fat2[0] = MEDIA_DESCRIPTOR; fat2[1] = 0xFF; fat2[2] = 0xFF;

   // ---- Cluster allocator --------------------------------------------------
   let nextCluster = 2; // first data cluster
   const dataArea = disk.subarray(DATA_START_BYTE);

   function allocClusters(numClusters) {
      const first = nextCluster;
      for (let c = 0; c < numClusters; c++) {
         const cl = nextCluster + c;
         if (c < numClusters - 1) {
            setFat12Entry(fat1, cl, cl + 1);
            setFat12Entry(fat2, cl, cl + 1);
         } else {
            setFat12Entry(fat1, cl, 0xFFF);
            setFat12Entry(fat2, cl, 0xFFF);
         }
      }
      nextCluster += numClusters;
      return first;
   }

   function writeFile(data) {
      const numClusters = Math.ceil(data.length / SECTOR_SIZE);
      if (numClusters === 0) return 0;
      const firstCluster = allocClusters(numClusters);
      const dataOff = (firstCluster - 2) * SECTOR_SIZE;
      dataArea.set(data, dataOff);
      // Zero-pad remaining bytes in last cluster
      const remainder = data.length % SECTOR_SIZE;
      if (remainder > 0 && remainder < SECTOR_SIZE) {
         for (let i = remainder; i < SECTOR_SIZE; i++) {
            dataArea[dataOff + data.length - remainder + i] = 0;
         }
      }
      return firstCluster;
   }

   function writeDirBlock(entries, parentCluster) {
      // entries: array of 32-byte Uint8Array
      // Returns first cluster of the directory data
      const numClusters = Math.ceil(entries.length * 32 / SECTOR_SIZE);
      const firstCluster = allocClusters(numClusters);

      // Write entries to data area
      const dataOff = (firstCluster - 2) * SECTOR_SIZE;
      for (let i = 0; i < entries.length; i++) {
         dataArea.set(entries[i], dataOff + i * 32);
      }

      // Fix . entry to point to this directory's first cluster
      const dotEntry = dataArea.subarray(dataOff, dataOff + 32);
      const dotDv = new DataView(dotEntry.buffer, dotEntry.byteOffset, 32);
      dotDv.setUint16(20, firstCluster & 0xFFFF, true);

      // Fix .. entry to point to parent
      const dotDotEntry = dataArea.subarray(dataOff + 32, dataOff + 64);
      const dotDotDv = new DataView(dotDotEntry.buffer, dotDotEntry.byteOffset, 32);
      dotDotDv.setUint16(20, (parentCluster || 0) & 0xFFFF, true);

      return firstCluster;
   }

   // ---- Build all file data ------------------------------------------------
   const numSamples = validSamples.length;

   // Build SMPL files and SBNK files
   const smplFiles = [];
   const sbnkFiles = [];
   for (let i = 0; i < numSamples; i++) {
      const smp = validSamples[i];
      const sampleRate = smp.sampleRate || YAMAHA_SAMPLE_RATE;
      const baseName = (smp.name || `SAMPLE${i + 1}`).substring(0, 16);
      const fxxx = `F${String(i + 1).padStart(3, '0')}`;

      smplFiles.push({
         fxxx,
         name: baseName,
         data: buildSmplFile(baseName, smp.pcm, sampleRate),
      });
      sbnkFiles.push({
         fxxx,
         name: baseName,
         data: buildSbnkFile(baseName, baseName, { originalKey: smp.originalKey || 60 }),
      });
   }

   // Build 0000 files
   // SBNK 0000: sample name → Fxxx mapping
   const sbnk0000Entries = sbnkFiles.map(s => ({ name: s.name, path: s.fxxx }));
   const sbnk0000Data = build0000File(sbnk0000Entries);

   // SMPL 0000: waveform name → Fxxx mapping
   const smpl0000Entries = smplFiles.map(s => ({ name: s.name, path: s.fxxx }));
   const smpl0000Data = build0000File(smpl0000Entries);

   // Disk 0000: volume name → F001 mapping + _DSKNAME → F002
   // Disk name file is numbered after all volume dirs (we have 1 volume: F001)
   const diskNameFile = `F${String(2).padStart(3, '0')}`; // F002
   const disk0000Entries = [
      { name: volumeName, path: 'F001' },       // volume 1 → F001 dir
      { name: '_DSKNAME', path: diskNameFile },  // disk name → F002 file
   ];
   const disk0000Data = build0000File(disk0000Entries);

   // Disk name file (F009)
   const diskNameData = buildDiskNameFile(diskName);

   // ---- Allocate clusters and build directory structure --------------------
   // We need to allocate clusters in order, but directory entries need to
   // reference cluster numbers that are allocated later. So we do two passes:
   // 1. Allocate all clusters first (files + directories)
   // 2. Build directory entries with the known cluster numbers

   // Allocate SMPL files
   const smplFileClusters = smplFiles.map(f => writeFile(f.data));

   // Allocate SBNK files
   const sbnkFileClusters = sbnkFiles.map(f => writeFile(f.data));

   // Allocate 0000 files
   const smpl0000Cluster = writeFile(smpl0000Data);
   const sbnk0000Cluster = writeFile(sbnk0000Data);
   const disk0000Cluster = writeFile(disk0000Data);

   // Allocate disk name file
   const diskNameCluster = writeFile(diskNameData);

   // ---- Build directory entries --------------------------------------------
   // We need to build directories bottom-up to know cluster numbers.

   // SMPL directory entries: . + .. + 0000 + F001 + F002 + ...
   const smplDirEntries = [];
   smplDirEntries.push(buildDirEntry('.', '   ', 0, 0, true));      // placeholder, fixed in writeDirBlock
   smplDirEntries.push(buildDirEntry('..', '   ', 0, 0, true));     // placeholder, fixed in writeDirBlock
   smplDirEntries.push(buildDirEntry('0000', '   ', smpl0000Cluster, smpl0000Data.length, false));
   for (let i = 0; i < smplFiles.length; i++) {
      const fat = toFatName(smplFiles[i].fxxx, '');
      smplDirEntries.push(buildDirEntry(fat.name, fat.ext, smplFileClusters[i], smplFiles[i].data.length, false));
   }

   // SBNK directory entries: . + .. + 0000 + F001 + F002 + ...
   const sbnkDirEntries = [];
   sbnkDirEntries.push(buildDirEntry('.', '   ', 0, 0, true));
   sbnkDirEntries.push(buildDirEntry('..', '   ', 0, 0, true));
   sbnkDirEntries.push(buildDirEntry('0000', '   ', sbnk0000Cluster, sbnk0000Data.length, false));
   for (let i = 0; i < sbnkFiles.length; i++) {
      const fat = toFatName(sbnkFiles[i].fxxx, '');
      sbnkDirEntries.push(buildDirEntry(fat.name, fat.ext, sbnkFileClusters[i], sbnkFiles[i].data.length, false));
   }

   // We need to know the volume directory's cluster to set .. in SBNK/SMPL dirs.
   // And we need to know SBNK/SMPL dir clusters to set entries in the volume dir.
   // And we need the disk dir cluster to set .. in the volume dir.
   // And we need the volume dir cluster to set entries in the disk dir.

   // So the allocation order is:
   // 1. Disk dir (we know its cluster)
   // 2. Volume dir (we know its cluster, and .. points to disk dir)
   // 3. SBNK dir (we know its cluster, and .. points to volume dir)
   // 4. SMPL dir (we know its cluster, and .. points to volume dir)

   // But we already allocated file clusters above. The directory clusters
   // come after. Let's allocate them now.

   // Actually, we need to reorganize. Let me allocate directories first,
   // then we can build all entries with correct cluster numbers.

   // Hmm, but we already wrote the file data. The directory clusters will
   // come after the file data clusters. That's fine — FAT12 doesn't require
   // any particular ordering.

   // Allocate volume directory (will contain SBNK + SMPL subdirs)
   // We need to know how many entries: . + .. + SBNK + SMPL = 4
   const volDirCluster = allocClusters(1); // 4 entries fit in 1 cluster (16 entries max)

   // Allocate SBNK directory
   const sbnkNumEntries = 2 + 1 + sbnkFiles.length; // . + .. + 0000 + files
   const sbnkDirClusters = Math.ceil(sbnkNumEntries * 32 / SECTOR_SIZE);
   const sbnkDirCluster = allocClusters(sbnkDirClusters);

   // Allocate SMPL directory
   const smplNumEntries = 2 + 1 + smplFiles.length;
   const smplDirClusters = Math.ceil(smplNumEntries * 32 / SECTOR_SIZE);
   const smplDirCluster = allocClusters(smplDirClusters);

   // Allocate disk directory
   // Entries: . + .. + 0000 + F009 + F001 = 5
   const diskDirCluster = allocClusters(1);

   // ---- Now write directory data with correct cluster numbers --------------

   // Write SBNK directory
   {
      const off = (sbnkDirCluster - 2) * SECTOR_SIZE;
      // Fix . and .. entries
      sbnkDirEntries[0] = buildDirEntry('.', '   ', sbnkDirCluster, 0, true);
      sbnkDirEntries[1] = buildDirEntry('..', '   ', volDirCluster, 0, true);
      for (let i = 0; i < sbnkDirEntries.length; i++) {
         dataArea.set(sbnkDirEntries[i], off + i * 32);
      }
   }

   // Write SMPL directory
   {
      const off = (smplDirCluster - 2) * SECTOR_SIZE;
      smplDirEntries[0] = buildDirEntry('.', '   ', smplDirCluster, 0, true);
      smplDirEntries[1] = buildDirEntry('..', '   ', volDirCluster, 0, true);
      for (let i = 0; i < smplDirEntries.length; i++) {
         dataArea.set(smplDirEntries[i], off + i * 32);
      }
   }

   // Write volume directory (F001)
   {
      const off = (volDirCluster - 2) * SECTOR_SIZE;
      const volEntries = [
         buildDirEntry('.', '   ', volDirCluster, 0, true),
         buildDirEntry('..', '   ', diskDirCluster, 0, true),
         buildDirEntry('SBNK', '   ', sbnkDirCluster, 0, true),
         buildDirEntry('SMPL', '   ', smplDirCluster, 0, true),
      ];
      for (let i = 0; i < volEntries.length; i++) {
         dataArea.set(volEntries[i], off + i * 32);
      }
   }

   // Write disk directory (DISKHEX)
   {
      const off = (diskDirCluster - 2) * SECTOR_SIZE;
      const diskDirEntries = [
         buildDirEntry('.', '   ', diskDirCluster, 0, true),
         buildDirEntry('..', '   ', 0, 0, true), // parent is root (cluster 0)
         buildDirEntry('0000', '   ', disk0000Cluster, disk0000Data.length, false),
         buildDirEntry(diskNameFile, '   ', diskNameCluster, diskNameData.length, false),
         buildDirEntry('F001', '   ', volDirCluster, 0, true),
      ];
      for (let i = 0; i < diskDirEntries.length; i++) {
         dataArea.set(diskDirEntries[i], off + i * 32);
      }
   }

   // ---- Root directory -----------------------------------------------------
   // Volume label entry
   const volEntry = new Uint8Array(32);
   writeAscii(volEntry, 0, 11, diskName.padEnd(11, ' ').substring(0, 11));
   volEntry[11] = 0x08; // volume label attribute
   disk.set(volEntry, ROOT_DIR_OFFSET);

   // Disk directory entry in root
   const diskDirName = generateDiskDirName(diskName);
   const fatDiskDir = toFatName(diskDirName, '');
   const rootEntry = buildDirEntry(fatDiskDir.name, fatDiskDir.ext, diskDirCluster, 0, true);
   disk.set(rootEntry, ROOT_DIR_OFFSET + 32);

   // ---- Copy FAT1 to FAT2 --------------------------------------------------
   fat2.set(fat1);

   // ---- Debug info ---------------------------------------------------------
   console.log(`[YAMAHA] buildYamahaDisk: ${numSamples} samples, ` +
      `clusters used: ${nextCluster - 2}/${MAX_CLUSTERS}, disk: ${DISK_SIZE} bytes`);
   console.log(`[YAMAHA] disk dir: "${diskDirName}" (cluster ${diskDirCluster}), ` +
      `volume: "${volumeName}" (cluster ${volDirCluster})`);
   console.log(`[YAMAHA] SBNK dir: cluster ${sbnkDirCluster}, SMPL dir: cluster ${smplDirCluster}`);
   for (let i = 0; i < numSamples; i++) {
      const smp = validSamples[i];
      const smplSize = YAMAHA_HEADER_SIZE + smp.pcm.length * 2;
      console.log(`[YAMAHA] sample ${i}: name="${smp.name}", ` +
         `pcm=${smp.pcm.length} samples, SMPL=${smplSize}B, SBNK=${YAMAHA_SBNK_FILE_SIZE}B`);
   }

   return disk;
}

/**
 * Generate an 8-character hex disk directory name from the disk name.
 * Yamaha uses hex names like "1DDAAB50" for disk directories.
 */
function generateDiskDirName(diskName) {
   // Simple hash from disk name → 8 hex chars
   let hash = 0xA0000000;
   const str = diskName || 'YAMAHA';
   for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
   }
   // Ensure uppercase hex, 8 chars
   return (hash >>> 0).toString(16).toUpperCase().padStart(8, '0').substring(0, 8);
}
